import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";

export function ShippingDetailsViewModelV2(shippingId, logout, addSnackbar) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [shipping, setShipping] = useState(null);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [sunCustomers, setSunCustomers] = useState([]);

  const getShippingDetails = async function () {
    let callResult = await makePostApiCall(`fetchShipping/${shippingId}`);
    if (callResult.success) {
      const orderDetails = await onFetchOrderItems(callResult.data.order_id);
      if (orderDetails.success) {
        const order = orderDetails.data;
        console.log("order", order);
        setShipping({
          ...callResult.data,
          order_id: order.id,
          customer: order.customer,
          orderItems: [...order.products],
          shipped_subtotal_dollar_amount: order.shipped_subtotal_dollar_amount,
          shipped_total_dollar_amount: order.shipped_total_dollar_amount,
        });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onFetchOrderItems = async function (id) {
    let callResult = await makePostApiCall(`fetchOrder/${id}`);
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const updateShippingApiCall = async function (payload, isSubAction = false) {
    showProgressModal("Updating shipping...");
    let callResult = await makePostApiCall(
      `updateShipping/${shippingId}`,
      { 
        ...payload, 
        is_prepaid: payload.is_prepaid ? "1" : "0" 
      }
    );
    if (callResult.success) {
      if (!isSubAction) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
      }
      hideProgressModal();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onFetchPackagedItemByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addPackagedItemToOrderItem = async function (
    shippingId,
    orderItem,
    packagedItem
  ) {
    const payload = {
      shippingId: shippingId,
      orderItem: orderItem,
      packagedItem: packagedItem,
    };
    let callResult = await makePostApiCall(
      "addPackagedItemToOrderItem",
      payload
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package added to shipping",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShippingItemApiCall = async function (id) {
    let callResult = await makePostApiCall(`deleteShippingItem/${id}`);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addSkidLabelToOrderItem = async function (
    shippingId,
    orderItemId,
    skidLabelNumber
  ) {
    const payload = {
      shippingId: shippingId,
      orderItemId: orderItemId,
      packaged_items: skidLabelNumber,
    };
    let callResult = await makePostApiCall(
      "addSkidLabelNumberToOrderItem",
      payload
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Packages added to shipping",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getEmployeesForDropdown = async function (type, department) {
    const payload = { department: department, type: type };
    let callResult = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShipping = async function () {
    let callResult = await makePostApiCall(`shippings/delete/${shippingId}`);

    if (callResult.success) {
      window.location = "/shippings";
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.error);
    }
  };

  const printShipping = async function (formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    showProgressModal("Printing shipping slip...");
    
    try {
      let callResult = await makePostApiCall(`printShipping/${shippingId}`);

      hideProgressModal();
      if (callResult.success) {
        window.open(callResult.data.location, "_blank");
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
      }
    } catch (error) {
      hideProgressModal();
      showErrorModal("Error", "An error occurred while printing the shipping label.");
    }
  };

  const quickAddPackagedItemToOrderItem = async function (newValues) {
    let callResult = await makePostApiCall(
      "quickAddPackagedItemToOrderItemV2",
      { ...newValues, quantity: parseInt(newValues.quantity), shipping_id: shippingId }
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package(s) added to shipping",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteQuickScanShippingItemByLot = async function (payload) {
    let callResult = await makePostApiCall(
      `deleteQuickScanShippingItemByLot`,
      payload
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("We could not delete the item");
    }
    return callResult;
  };

  const exportOrderForNetSuite = async function () {
    let callResult = await makePostApiCall(`exportOrderForNetSuite`, {
      shipments: [shippingId],
    });
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getOrderItemsOnly = async function () {
    let callResult = await makeGetApiCall(
      `getOrderItemsOnlyV2/${shipping.order_id}`
    );
    if (callResult.success) {
      setRowDetails({});
      setLoadingRows({});
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.id]: true }));

    let callResult = await makeGetApiCall(
      `getOrderItemShipments/${rowData.id}`
    );
    if (callResult.success) {
      setRowDetails({
        ...rowDetails,
        [rowData.id]: callResult.data.shippingItems,
      });
      setLoadingRows({ ...loadingRows, [rowData.id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getSunCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`getSunCustomersForDropdown`);
    if (callResult.success) {
      setSunCustomers(callResult.data);
    } else {
      showErrorModal(callResult.data.message);
    }
  };

  const sendShippingToQuickbooks = async function (formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    showProgressModal("Sending shipping to Quickbooks...");
    let callResult = await makePostApiCall(`sunDistribution/sendShippingToQuickbooks`,
      {
        shipment_id: parseInt(shippingId),
      }
    );
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(new SnackbarData(generateUniqueNumber(), "Shipping sent to Quickbooks", SnackbarType.Success));
      setShipping({
        ...shipping,
        sun_quickbooks_po_id: callResult.data.purchase_order.qb_po_id,
        sun_quickbooks_invoice_id: callResult.data.invoice.sun_quickbooks_invoice_id,
        invoice_number: callResult.data.invoice.invoice_number,
      });
    } else {
      showErrorModal(callResult.data.message);
    }
  };  

  const printShippingLabel = async function (orderItemId) {
    showProgressModal("Printing shipping label...");
    let callResult = await makePostApiCall(`sunDistribution/printSunShippingLabel`,
      {
        shipping_id: parseInt(shippingId),
        order_item_id: parseInt(orderItemId),
      }
    );
    hideProgressModal();
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      showErrorModal(callResult.data.message);
    }
  };      

  const calculateFreightWeight = async function (palletCount, formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return updateResult;
    }

    showProgressModal("Calculating freight weight...");
    let callResult = await makePostApiCall(`sunDistribution/calculateWalmartFreight`,
      {
        shipment_id: parseInt(shippingId),
        pallet_count: parseInt(palletCount),
      }
    );
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(new SnackbarData(generateUniqueNumber(), "Freight weight calculated", SnackbarType.Success));
    } else {
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };    

  const generateBolDocument = async function (formValues) {
    // Save form data first
    const updateResult = await updateShippingApiCall(formValues, true);
    if (!updateResult.success) {
      return;
    }

    showProgressModal("Generating BOL document...");
    let callResult = await makePostApiCall(`sunDistribution/createWalmartBol/${shippingId}`);
    hideProgressModal();
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      showErrorModal(callResult.data.message);
    }
  };  

  const generateBolNumber = async function () {
    showProgressModal("Generating BOL number...");
    let callResult = await makeGetApiCall(`sunDistribution/generateNextWalmartBolNumber`);
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(new SnackbarData(generateUniqueNumber(), "BOL number generated", SnackbarType.Success));
      setShipping({
        ...shipping,
        sun_bol_number: callResult.data.next_bol_number,
      });
    } else {
      showErrorModal(callResult.data.message);
  };
    return callResult;
  };

  const updateOrderItemUnitCost = async function (orderItemId, unitPrice) {

    showProgressModal("Updating unit price...");
    let callResult = await makePostApiCall(`updateOrderItemUnitCost`, {
      order_item_id: parseInt(orderItemId),
      unit_price: parseFloat(unitPrice),
    });
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Unit price updated",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  return {
    onFetchOrderItems,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateShippingApiCall,
    shipping,
    onFetchPackagedItemByBarcode,
    addPackagedItemToOrderItem,
    getShippingDetails,
    deleteShippingItemApiCall,
    addSkidLabelToOrderItem,
    employeeOptions,
    getEmployeesForDropdown,
    deleteShipping,
    printShipping,
    quickAddPackagedItemToOrderItem,
    deleteQuickScanShippingItemByLot,
    exportOrderForNetSuite,
    getOrderItemsOnly,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    sunCustomers,
    getSunCustomersForDropdown,
    sendShippingToQuickbooks,
    printShippingLabel,
    calculateFreightWeight,
    generateBolDocument,
    generateBolNumber,
    updateOrderItemUnitCost
  };
}
